/**
 * Lightweight client-side error reporter for the patient app.
 * Fire-and-forget — never blocks the UI, never throws, never cascades.
 * POSTs to the blhWebServer `/api/client-errors` endpoint; errors land in
 * CloudWatch via Pino with `source: "patientApp"` and `clientError: true`.
 */

let currentTenant = null;

export function setReportErrorTenant(tenant) {
  currentTenant = tenant;
}

const SERVER_LOG_URL =
  process.env.REACT_APP_SERVER_LOG_URL ||
  "https://server.brightlight.ai/api/client-errors";

const CLIENT_ERROR_TOKEN = process.env.REACT_APP_CLIENT_ERROR_TOKEN;

const RELEASE =
  process.env.REACT_APP_RELEASE ||
  process.env.REACT_APP_GIT_SHA ||
  (process.env.NODE_ENV === "development" ? "dev" : "unknown");

const REPORT_IN_DEV = process.env.REACT_APP_REPORT_ERRORS_IN_DEV === "true";

// Prevent the same error from flooding the collector.
const recent = new Map();
const DEDUPE_MS = 5000;
const MAX_BREADCRUMBS = 20;

function simpleHash(value) {
  const text = String(value || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

function getLocationDiagnostic() {
  if (typeof window === "undefined") return {};
  return {
    path: window.location.pathname,
    searchHash: simpleHash(window.location.search),
    searchLength: window.location.search.length,
  };
}

function getWindowDiagnostics() {
  if (typeof window === "undefined") return {};
  return window.__brightlightDiagnostics || {};
}

function safeJson(value, max = 1000) {
  try {
    const serialized = JSON.stringify(value);
    return serialized.length > max ? `${serialized.slice(0, max)}...` : serialized;
  } catch {
    return "[unserializable]";
  }
}

function buildBreadcrumbs(opts) {
  const breadcrumbs = [];
  if (Array.isArray(opts.breadcrumbs)) {
    breadcrumbs.push(...opts.breadcrumbs);
  }
  if (opts.extra?.componentStack) {
    breadcrumbs.push({
      type: "componentStack",
      value: String(opts.extra.componentStack).slice(0, 1200),
    });
  }
  if (opts.extra) {
    breadcrumbs.push({
      type: "extra",
      value: safeJson(opts.extra),
    });
  }
  breadcrumbs.push({
    type: "diagnostics",
    value: safeJson({
      location: getLocationDiagnostic(),
      ...getWindowDiagnostics(),
    }),
  });
  return breadcrumbs.slice(-MAX_BREADCRUMBS);
}

// Said once per session, not once per error.
let reportedCollectorRefusal = false;

export async function reportError(err, opts = {}) {
  try {
    const isDev = process.env.NODE_ENV === "development";
    if (isDev && !REPORT_IN_DEV) return;

    const errorName =
      err instanceof Error
        ? err.name
        : typeof err === "string"
        ? "Error"
        : "UnknownError";
    const errorMessage =
      err instanceof Error
        ? err.message
        : typeof err === "string"
        ? err
        : "Unknown error";
    const stack = err instanceof Error ? err.stack : undefined;
    const breadcrumbs = buildBreadcrumbs(opts);

    if (typeof console !== "undefined") {
      console.error("[Brightlight patient error]", {
        errorName,
        errorMessage,
        stack,
        diagnostics: getWindowDiagnostics(),
        location: getLocationDiagnostic(),
        breadcrumbs,
      });
    }

    // A missing token used to end the function here, and that early return is
    // why this collector has never delivered a single report from production:
    // `REACT_APP_CLIENT_ERROR_TOKEN` is not set in the deploy, so the whole tail
    // was dead code — the minifier removed it, and the deployed bundle does not
    // even contain the header name. Two total outages of the patient form flow
    // went unseen behind that, and the second one was after the first was
    // written up as an open item.
    //
    // So the report goes either way. The collector decides whether to keep it:
    // it accepts an untokened post outside production and answers 401 or 503
    // otherwise, and either of those is now said out loud rather than being the
    // difference between reporting and silence. A failure a collector rejects
    // is a configuration problem someone can fix; a failure that is never sent
    // is one nobody can see.
    const key = `${errorName}|${errorMessage}|${window.location.pathname}`;
    const now = Date.now();
    const last = recent.get(key);
    if (last && now - last < DEDUPE_MS) return;
    recent.set(key, now);
    if (recent.size > 100) {
      const cutoff = now - DEDUPE_MS;
      for (const [k, t] of recent) if (t < cutoff) recent.delete(k);
    }

    const body = {
      source: "patientApp",
      release: RELEASE,
      url: window.location.pathname,
      errorName,
      errorMessage,
      stack,
      tenant: opts.tenant != null ? opts.tenant : currentTenant,
      extra: opts.extra,
      breadcrumbs,
    };

    const response = await fetch(SERVER_LOG_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(CLIENT_ERROR_TOKEN ? {"x-client-error-token": CLIENT_ERROR_TOKEN} : {}),
      },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: "omit",
    }).catch(() => null);

    if (response && !response.ok && typeof console !== "undefined") {
      // Once per session: the point is to be noticed, not to be noisy.
      if (!reportedCollectorRefusal) {
        reportedCollectorRefusal = true;
        console.error(
          `[Brightlight patient error] the error collector refused this report (${response.status}). ` +
            (CLIENT_ERROR_TOKEN
              ? "REACT_APP_CLIENT_ERROR_TOKEN does not match the server's CLIENT_ERROR_TOKEN."
              : "REACT_APP_CLIENT_ERROR_TOKEN is not set in this build.") +
            " Client-side failures are not reaching anybody."
        );
      }
    }
  } catch {
    /* swallow */
  }
}

/**
 * Derive tenant from hostname (e.g. `havenpoint.brightlight.ai` → `havenpoint`).
 * The patient app has no TenantProvider, so we read it once at boot.
 */
export function deriveTenantFromHostname() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname || "";
  if (host === "localhost" || host.startsWith("127.")) return "havenpoint";
  const first = host.split(".")[0];
  return first || null;
}

let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  setReportErrorTenant(deriveTenantFromHostname());
  window.addEventListener("error", (e) => {
    void reportError(e.error || e.message || "window.onerror");
  });
  window.addEventListener("unhandledrejection", (e) => {
    void reportError(e.reason || "unhandledrejection");
  });
}
